import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Copy } from "lucide-react";
import { NotesEditor } from "./NotesEditor";
import { useNotesEditor } from "@/context/NotesEditorContext";
import { getNotesAsText } from "@/lib/notes/exportNotes";
import { toast } from "sonner";

interface NotesFloaterProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}

export const NotesFloater: React.FC<NotesFloaterProps> = ({
  isOpen,
  onClose,
  title = "Notes",
}) => {
  const { editor } = useNotesEditor();
  const [position, setPosition] = useState({ x: 760, y: 200 });
  const [size, setSize] = useState({ width: 400, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const floaterRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  }, [position]);

  const handleResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(direction);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.current.x));
        const newY = Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragOffset.current.y));
        setPosition({ x: newX, y: newY });
      }

      if (isResizing) {
        const rect = floaterRef.current?.getBoundingClientRect();
        if (!rect) return;

        let newWidth = size.width;
        let newHeight = size.height;
        let newX = position.x;
        let newY = position.y;

        if (isResizing.includes('right')) {
          newWidth = Math.max(300, e.clientX - position.x);
        }
        if (isResizing.includes('left')) {
          const delta = position.x - e.clientX;
          newWidth = Math.max(300, size.width + delta);
          if (newWidth !== size.width) {
            newX = e.clientX;
          }
        }
        if (isResizing.includes('bottom')) {
          newHeight = Math.max(200, e.clientY - position.y);
        }
        if (isResizing.includes('top')) {
          const delta = position.y - e.clientY;
          newHeight = Math.max(200, size.height + delta);
          if (newHeight !== size.height) {
            newY = e.clientY;
          }
        }

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, position, size]);

  const handleCopyNotes = async () => {
    if (!editor) {
      toast.error("Notes editor not ready");
      return;
    }
    try {
      const text = getNotesAsText(editor);
      if (!text.trim()) {
        toast.error("No notes to copy");
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.success("Notes copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy notes");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={floaterRef}
      className="fixed z-50 pointer-events-auto touch-none animate-scale-in"
      style={{
        left: 0,
        top: 0,
        width: size.width,
        height: size.height,
        transform: `translateX(${position.x}px) translateY(${position.y}px)`,
      }}
    >
      <div className="group h-full rounded-lg border border-primary/10 shadow-2xl bg-background/90 backdrop-blur-lg backdrop-saturate-150">
        {/* Header */}
        <div
          className="relative flex h-8 w-full items-center justify-center rounded-t-lg px-3 cursor-grab select-none"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          {/* Close button */}
          <div
            className="group/close absolute left-2 top-1/2 -translate-y-1/2 flex cursor-pointer items-center justify-center opacity-100 transition-all group-hover:opacity-100 xl:opacity-0"
            onClick={onClose}
          >
            <div className="flex h-3 w-3 items-center justify-center rounded-full border border-incorrect/60 bg-incorrect transition-all hover:scale-110">
              <X className="h-2.5 w-2.5 text-incorrect/60 opacity-0 transition-all group-hover/close:text-background group-hover/close:opacity-100" />
            </div>
          </div>

          {/* Title */}
          <div className="flex max-w-xs items-center truncate text-sm text-muted-foreground transition-colors group-hover:text-primary">
            <span className="max-w-xs truncate">{title}</span>
          </div>

          {/* Copy button */}
          <button
            onClick={handleCopyNotes}
            className="absolute right-1 inline-flex h-6 w-6 items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent text-muted-foreground opacity-100 transition-opacity hover:text-foreground group-hover:opacity-100 xl:opacity-0"
            title="Copy notes"
          >
            <Copy className="h-3 w-3" />
          </button>
        </div>

        {/* Editor content */}
        <div className="h-[calc(100%-2rem)] overflow-hidden rounded-b-lg">
          <div className="h-full w-full">
            <NotesEditor />
          </div>
        </div>
      </div>

      {/* Resize handles */}
      <div
        className="absolute -bottom-1 -right-1 h-4 w-4 z-10 cursor-nwse-resize"
        onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
      />
      <div
        className="absolute -bottom-1 -left-1 h-4 w-4 z-10 cursor-nesw-resize"
        onMouseDown={(e) => handleResizeStart(e, 'bottom-left')}
      />
      <div
        className="absolute -right-1 -top-1 h-4 w-4 z-10 cursor-nesw-resize"
        onMouseDown={(e) => handleResizeStart(e, 'top-right')}
      />
      <div
        className="absolute -left-1 -top-1 h-4 w-4 z-10 cursor-nwse-resize"
        onMouseDown={(e) => handleResizeStart(e, 'top-left')}
      />
      <div
        className="absolute -top-1 left-4 right-4 h-3 z-10 cursor-ns-resize"
        onMouseDown={(e) => handleResizeStart(e, 'top')}
      />
      <div
        className="absolute -bottom-1 left-4 right-4 h-3 z-10 cursor-ns-resize"
        onMouseDown={(e) => handleResizeStart(e, 'bottom')}
      />
      <div
        className="absolute -right-1 bottom-4 top-4 w-3 z-10 cursor-ew-resize"
        onMouseDown={(e) => handleResizeStart(e, 'right')}
      />
      <div
        className="absolute -left-1 bottom-4 top-4 w-3 z-10 cursor-ew-resize"
        onMouseDown={(e) => handleResizeStart(e, 'left')}
      />
    </div>
  );
};
